import {AsyncMenuButton} from "../../../../common/contextMenu/AsyncMenuButton.tsx";
import i18n from "i18next";
import {
    faBan,
    faCartShopping,
    faChartColumn,
    faCheckSquare,
    faClone,
    faCommentDollar,
    faCopy,
    faEnvelope,
    faEye,
    faEyeSlash,
    faHandPointRight,
    faIcicles,
    faLink,
    faPercent,
    faPrint,
    faProjectDiagram,
    faShare,
    faUndo,
    faUniversity,
} from "@fortawesome/pro-regular-svg-icons";
import {CustomerOrderActionApi} from "src/api/generated/erp/sales/customerOrder/api/customerOrderActionApi.ts";
import {downloadFile} from "src/utils/fileDownloading.ts";
import {OpenObjectChangeLogButton} from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import {
    CustomerOrderDataGridApi,
    CustomerOrderDataGridApi_CustomerOrderDto,
} from "src/api/generated/erp/sales/customerOrder/api/customerOrderDataGridApi.ts";
import {ServerSideDataGridModelRefreshFunc} from "../../../../common/dataGrid/gridModel/useServerSideDataGridModel";
import {useConfirmDialog} from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import {useMessageDialog} from "src/components/common/dialogs/messageDialog/MessageDialogContext.tsx";
import {useTenantCustomizations} from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import {useInputDialog} from "src/components/common/dialogs/input/useInputDialog.tsx";
import {
    connectCustomerOrderToProject,
    copyCustomerOrderTasks,
    createProjectForCustomerOrder,
} from "src/components/views/erp/sales/customerOrder/customerOrderActions.tsx";
import {AavoDataGridRowContextMenuParams} from "src/components/common/dataGrid/types";
import {CustomerOrderReleaseApi} from "src/api/generated/erp/sales/customerOrder/api/customerOrderReleaseApi.ts";
import {CustomerOrdersPageContext} from "src/components/views/erp/sales/customerOrder/CustomerOrdersPageContext.tsx";
import {useContext} from "react";
import {useGenericDialog} from "src/components/common/dialogs/GenericDialogContext.ts";
import {OpenCustomerOrderProjectInTreeViewButton} from "src/components/views/erp/utilComponents/OpenCustomerOrderProjectInTreeViewButton.tsx";
import {
    CustomerOrderAccountingForm
} from "src/components/views/erp/sales/customerOrder/CustomerOrderAccountingForm.tsx";
import {openFormOnDialog} from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import {cancelCustomerOrder} from "src/components/views/erp/sales/customerOrder/actions/cancelCustomerOrder.ts";
import {OpenCustomerButton} from "src/components/views/erp/utilComponents/OpenCustomerButton.tsx";
import {OpenConnectedWorkspacesButton} from "src/components/views/erp/utilComponents/OpenConnectedWorkspacesButton.tsx";
import {OpenSalesLeadButton} from "src/components/views/erp/utilComponents/OpenSalesLeadButton.tsx";
import {CopyCustomerOrderForm} from "src/components/views/erp/sales/customerOrder/CopyCustomerOrderForm.tsx";
import {SendObjectDocumentsView} from "src/components/views/tenantMail/SendObjectDocumentsView.tsx";
import {AavoMenuDivider} from "src/components/common/menu/AavoMenuDivider.tsx";
import {
    CustomerOrderLineReservationView
} from "src/components/views/erp/sales/delivery/picking/CustomerOrderLineReservationView.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import {
    CustomerOrderLinkedPurchaseOrdersDataGrid,
} from "src/components/views/erp/sales/customerOrder/CustomerOrderLinkedPurchaseOrdersDataGrid.tsx";
import {SingleCustomerOfferView} from "src/components/views/erp/sales/customerOrderOffer/SingleCustomerOfferView.tsx";
import {
    CustomerOrderProfitabilityView
} from "src/components/views/erp/sales/customerOrder/profitability/CustomerOrderProfitabilityView.tsx";
import {defineAgentUiContextKey} from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { CustomUiActionObjectMenuItems } from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";
import { CustomerOrderProfitMarginApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderProfitMarginApi.ts";
import { PriceRecalculationActions } from "src/components/views/erp/sales/pricing/PriceRecalculationActions.tsx";
import { shouldShowPriceRecalculationActions } from "src/components/views/erp/sales/pricing/priceRecalculationActionVisibility.ts";

export interface CustomerOrdersDataGridContextMenuComponentsProps extends AavoDataGridRowContextMenuParams<CustomerOrderDataGridApi_CustomerOrderDto> {
    refreshData: ServerSideDataGridModelRefreshFunc<typeof CustomerOrderDataGridApi.searchCustomerOrders>;
}

export const CustomerOrdersDataGridContextMenuComponents = (
    props: CustomerOrdersDataGridContextMenuComponentsProps,
) => {
    const {refreshData, row, onlySingleRowSelected, allSelectedRows} = props;

    const confirm = useConfirmDialog();
    const {openDialog} = useGenericDialog();

    const tenantCustomizations = useTenantCustomizations();
    const tenantConfig = tenantCustomizations.tenantConfig;
    const salesCustomizations = tenantCustomizations.erp?.sales;

    const showInputDialog = useInputDialog();
	const showConfirmDialog = useConfirmDialog();
	const showMessageDialog = useMessageDialog();
    const hasPermission = useHasPermission();

    const customerOrdersPageContext = useContext(CustomerOrdersPageContext);
    const customerOrderLinesRefreshRef = customerOrdersPageContext?.customerOrderLinesRefreshRef;

    return (
        <>
            {[
                <CustomUiActionObjectMenuItems
                    key={"customFunctionButtons"}
                    view={"CUSTOMER_ORDERS"}
                    objectIds={allSelectedRows.map((order) => order.customerOrderId)}
                />,
                row.releasedAt == null && onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"release"}
                        label={i18n.t("release")}
                        icon={faShare}
                        onClick={releaseCustomerOrder}
                    />
                ),
                row.confirmedAt == null && onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"confirm"}
                        label={i18n.t("confirm")}
                        icon={faCheckSquare}
                        onClick={async () => {
                            const confirmed = await confirm({
                                message:
                                    row.priceRecalculationStatus === "RECALCULATION_REQUIRED" ?
                                        i18n.t("confirm_order_price_recalculation_required_confirmation")
                                    : undefined,
                            });
                            if (!confirmed) return;
                            await CustomerOrderActionApi.confirmCustomerOrder({
                                customerOrderId: row.customerOrderId,
                            });
                            await refreshData();
                        }}
                    />
                ),
                onlySingleRowSelected && row.confirmedAt != null && (
                    <AsyncMenuButton
                        key={"reconfirm"}
                        label={i18n.t("reconfirm")}
                        icon={faCheckSquare}
                        onClick={async () => {
                            const confirmed = await confirm({
                                message:
                                    row.priceRecalculationStatus === "RECALCULATION_REQUIRED" ?
                                        i18n.t("confirm_order_price_recalculation_required_confirmation")
                                    : undefined,
                            });
                            if (!confirmed) return;

                            await CustomerOrderActionApi.reconfirmCustomerOrder({
                                customerOrderId: row.customerOrderId,
                            });
                            await refreshData();
                        }}
                    />
                ),
                onlySingleRowSelected && tenantConfig.erp.customerOrderCheckingEnabled && row.checkedAt == null && (
                    <AsyncMenuButton
                        key={"check"}
                        label={i18n.t("check")}
                        icon={faEye}
                        onClick={async () => {
                            const confirmed = await confirm({
                                title: i18n.t("check"),
                                message: i18n.t("are_you_sure"),
                            });
                            if (!confirmed) return;
                            await CustomerOrderActionApi.checkCustomerOrder({
                                customerOrderId: row.customerOrderId,
                            });
                            await refreshData();
                        }}
                    />
                ),
                onlySingleRowSelected && tenantConfig.erp.customerOrderCheckingEnabled && row.checkedAt != null && (
                    <AsyncMenuButton
                        key={"revertCheck"}
                        label={i18n.t("revert_checking")}
                        icon={faEyeSlash}
                        onClick={async () => {
                            const confirmed = await confirm({
                                title: i18n.t("revert_checking"),
                                message: i18n.t("are_you_sure"),
                            });
                            if (!confirmed) return;
                            await CustomerOrderActionApi.revertCheckCustomerOrder({
                                customerOrderId: row.customerOrderId,
                            });
                            await refreshData();
                        }}
                    />
                ),
                onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"reserve"}
                        label={i18n.t("reserve")}
                        icon={faHandPointRight}
                        onClick={() => {
                            openDialog(() => ({
                                title: i18n.t("reserve"),
                                size: "xl",
                                agentContext: {
                                    key: defineAgentUiContextKey("erp.sales.customerOrderReservation.dialog"),
                                    pathSegments: [i18n.t("reserve")],
                                    data: {
                                        customerOrderId: row.customerOrderId,
                                        customerId: row.customerId,
                                    },
                                },
                                content: <CustomerOrderLineReservationView customerOrderId={row.customerOrderId}/>,
                            }));
                        }}
                    />
                ),
                onlySingleRowSelected && tenantConfig.erp.customerOrderFreezingEnabled && row.frozenAt == null && (
                    <AsyncMenuButton
                        key={"freeze"}
                        label={i18n.t("freeze")}
                        icon={faIcicles}
                        onClick={async () => {
                            const confirmed = await confirm({
                                title: i18n.t("freeze"),
                                message: i18n.t("are_you_sure"),
                            });
                            if (!confirmed) return;
                            await CustomerOrderActionApi.freezeCustomerOrder({
                                customerOrderId: row.customerOrderId,
                            });
                            await refreshData();
                        }}
                    />
                ),
                onlySingleRowSelected && tenantConfig.erp.customerOrderFreezingEnabled && row.frozenAt != null && (
                    <AsyncMenuButton
                        key={"revertFreeze"}
                        label={i18n.t("revert_freeze")}
                        icon={faUndo}
                        onClick={async () => {
                            const confirmed = await confirm({
                                title: i18n.t("revert_freeze"),
                                message: i18n.t("are_you_sure"),
                            });
                            if (!confirmed) return;
                            await CustomerOrderActionApi.unfreezeCustomerOrder({
                                customerOrderId: row.customerOrderId,
                            });
                            await refreshData();
                        }}
                    />
                ),
                row.customerOrderState === "INITIAL" && onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"cancelCustomerOrder"}
                        label={i18n.t("cancel")}
                        icon={faBan}
                        onClick={async () => {
                            const cancelled = await cancelCustomerOrder({
                                customerOrderId: row.customerOrderId,
                                showConfirmDialog: confirm,
                            });
                            if (cancelled) await refreshData();
                        }}
                    />
                ),
                salesCustomizations?.customerOrderContextMenuComponents?.(props),
                onlySingleRowSelected &&
                    hasPermission(Permissions.Sales.CustomerOrders.MANAGE) &&
                    row.customerOrderState !== "CANCELLED" &&
                    shouldShowPriceRecalculationActions(
                        row.customerPriceGroupPricingMethod,
                        row.priceRecalculationStatus,
                    ) && (
                        <PriceRecalculationActions
                            key={"priceRecalculation"}
                            status={row.priceRecalculationStatus}
                            recalculatePrices={() =>
                                CustomerOrderProfitMarginApi.recalculatePrices({
                                    customerOrderId: row.customerOrderId,
                                })
                            }
                            acknowledgePriceRecalculation={() =>
                                CustomerOrderProfitMarginApi.acknowledgePriceRecalculation({
                                    customerOrderId: row.customerOrderId,
                                })
                            }
                            onStatusChanged={async () => {
                                await Promise.all([refreshData(), customerOrderLinesRefreshRef?.refresh()]);
                            }}
                        />
                    ),
                <AavoMenuDivider key={"divider1"}/>,
                onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"sendDocuments"}
                        label={i18n.t("send_documents")}
                        icon={faEnvelope}
                        onClick={async () => {
                            if (row.customerOrderId == null) return;
                            openDialog(({closeDialog}) => ({
                                title: i18n.t("send_documents"),
                                size: "lg",
                                agentContext: {
                                    key: defineAgentUiContextKey("erp.sales.customerOrderDocuments.sendDialog"),
                                    pathSegments: [i18n.t("send_documents")],
                                    data: {
                                        customerOrderId: row.customerOrderId,
                                        customerId: row.customerId,
                                    },
                                },
                                content: (
                                    <SendObjectDocumentsView
                                        objectType={"CUSTOMER_ORDER"}
                                        objectId={row.customerOrderId}
                                        onCompletedSuccessfully={closeDialog}
                                    />
                                ),
                            }));
                        }}
                    />
                ),

                onlySingleRowSelected && row.sourceType == "MANUAL" && (
                    <AsyncMenuButton
                        key={"copyCustomerOrder"}
                        label={i18n.t("copy_customer_order")}
                        icon={faCopy}
                        onClick={async () => {
                            openFormOnDialog({
                                openDialog,
                                title: i18n.t("copy_customer_order"),
                                size: "sm",
                                component: CopyCustomerOrderForm,
                                agentContext: {
                                    key: defineAgentUiContextKey("erp.sales.customerOrder.copyFormDialog"),
                                    data: {
                                        sourceCustomerOrderId: row.customerOrderId,
                                        customerId: row.customerId,
                                    },
                                },
                                props: {
                                    sourceOrderId: row.customerOrderId,
                                },
                                onSubmit: async () => {
                                    await refreshData();
                                },
                            });
                        }}
                    />
                ),
                onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"copyTasks"}
                        label={i18n.t("copy_tasks")}
                        icon={faClone}
                        onClick={async () => {
                            await copyCustomerOrderTasks(row, showInputDialog);
                            await refreshData();
                        }}
                    />
                ),
                <AavoMenuDivider key={"divider2"}/>,
                tenantConfig.erp.projectEnabled && [
                    onlySingleRowSelected && (
                        <AsyncMenuButton
                            key={"createProjectButton"}
                            label={i18n.t("create_project")}
                            icon={faProjectDiagram}
                            onClick={async () => {
                                await createProjectForCustomerOrder(row, showInputDialog, showConfirmDialog);
                                await refreshData();
                            }}
                        />
                    ),
                    onlySingleRowSelected && (
                        <AsyncMenuButton
                            key={"connectToProject"}
                            label={i18n.t("connect_to_project")}
                            icon={faLink}
                            onClick={async () => {
                                await connectCustomerOrderToProject(row, showInputDialog, showConfirmDialog);
                                await refreshData();
                            }}
                        />
                    ),
                    onlySingleRowSelected && (
                        <OpenCustomerOrderProjectInTreeViewButton
                            key={"showProject"}
                            customerOrderId={row.customerOrderId}
                        />
                    ),
                    onlySingleRowSelected && hasPermission(Permissions.Purchasing.PurchaseOrders.READ) && (
                        <AsyncMenuButton
                            key={"connectedPurchaseOrders"}
                            label={i18n.t("connected_purchase_orders")}
                            icon={faCartShopping}
                            onClick={() => {
                                openDialog({
                                    title: i18n.t("connected_purchase_orders"),
                                    size: "xl",
                                    agentContext: {
                                        key: defineAgentUiContextKey("erp.sales.customerOrderLinkedPurchaseOrders.dialog"),
                                        pathSegments: [i18n.t("connected_purchase_orders")],
                                        data: {
                                            customerOrderId: row.customerOrderId,
                                            customerId: row.customerId,
                                        },
                                    },
                                    content: (
                                        <CustomerOrderLinkedPurchaseOrdersDataGrid
                                            customerOrderId={row.customerOrderId}
                                        />
                                    ),
                                });
                            }}
                        />
                    ),
                    <AavoMenuDivider key={"divider3"}/>,
                ],
                onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"printCustomerOrderConfirmation"}
                        label={i18n.t("order_confirmation")}
                        icon={faPrint}
                        onClick={async () => {
                            const customerOrderId = row.customerOrderId;
                            const fileHandle =
                                salesCustomizations?.printCustomerOrderConfirmation ?
                                    await salesCustomizations.printCustomerOrderConfirmation({
                                        customerOrderId,
                                        showConfirmDialog,
                                        showMessageDialog,
                                    })
                                :   await CustomerOrderActionApi.printCustomerOrderConfirmation({customerOrderId});
                            if (fileHandle) downloadFile(fileHandle);
                        }}
                    />
                ),
                onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"printCustomerOrderDegreeOfReadinessReport"}
                        label={i18n.t("degree_of_readiness_report")}
                        icon={faPercent}
                        onClick={async () => {
                            const apiFunc =
                                salesCustomizations?.printCustomerOrderDegreeOfReadinessReport ??
                                CustomerOrderActionApi.printCustomerOrderDegreeOfReadinessReport;

                            const fileHandle = await apiFunc({customerOrderId: row.customerOrderId});
                            downloadFile(fileHandle);
                        }}
                    />
                ),
                <AavoMenuDivider key={"divider4"}/>,
                onlySingleRowSelected && <OpenCustomerButton key={"showCustomer"} customerId={row.customerId}/>,
                onlySingleRowSelected && tenantConfig.workspacesEnabled && (
                    <OpenConnectedWorkspacesButton
                        key={"workspaces"}
                        connectedItemRef={{
                            itemType: "CUSTOMER_ORDER",
                            itemId: row.customerOrderId,
                        }}
                    />
                ),
                onlySingleRowSelected && row.customerOrderOfferId != null && (
                    <AsyncMenuButton
                        key={"showOffer"}
                        label={i18n.t("offer")}
                        icon={faCommentDollar}
                        onClick={async () => {
                            openDialog({
                                title: i18n.t("offer"),
                                agentContext: {
                                    key: defineAgentUiContextKey("erp.sales.customerOffer.customerOrdersDataGridContextMenuComponents.dialog"),
                                    pathSegments: [`${i18n.t("offer")} ${row.customerOrderOfferId}`],
                                    data: {
                                        customerOrderId: row.customerOrderId,
                                        customerOrderOfferId: row.customerOrderOfferId!,
                                    },
                                },
                                content: (
                                    <SingleCustomerOfferView
                                        customerOrderOfferId={row.customerOrderOfferId!!}
                                    />
                                ),
                            });
                        }}
                    />
                ),
                onlySingleRowSelected && row.salesLeadId != null && (
                    <OpenSalesLeadButton
                        key={"showSalesLead"}
                        salesLeadId={row.salesLeadId}
                    />
                ),
                onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"profitabilityAnalysis"}
                        label={i18n.t("profitability_analysis")}
                        icon={faChartColumn}
                        onClick={() => {
                            openDialog(({onDataDirtyStateChanged}) => ({
                                title: i18n.t("profitability_analysis"),
                                size: "fullscreen",
                                agentContext: {
                                    key: defineAgentUiContextKey("erp.sales.customerOrderProfitability.dialog"),
                                    pathSegments: [i18n.t("profitability_analysis")],
                                    data: {
                                        customerOrderId: row.customerOrderId,
                                        customerId: row.customerId,
                                    },
                                },
                                content: <CustomerOrderProfitabilityView
                                    customerOrderId={row.customerOrderId}
                                    onDataDirtyStateChanged={onDataDirtyStateChanged}
                                />,
                            }));
                        }}
                    />
                ),
                onlySingleRowSelected && (
                    <AsyncMenuButton
                        key={"accounting"}
                        label={i18n.t("accounting")}
                        icon={faUniversity}
                        onClick={() => {
                            openFormOnDialog({
                                openDialog,
                                title: i18n.t("accounting"),
                                size: "sm",
                                component: CustomerOrderAccountingForm,
                                agentContext: {
                                    key: defineAgentUiContextKey("erp.sales.customerOrderAccounting.formDialog"),
                                    data: {
                                        customerOrderId: row.customerOrderId,
                                        customerId: row.customerId,
                                    },
                                },
                                props: {
                                    customerOrderId: row.customerOrderId,
                                },
                            });
                        }}
                    />
                ),
                onlySingleRowSelected && row.customerOrderId != null && (
                    <OpenObjectChangeLogButton
                        key={"changeLog"}
                        objectRef={{objectType: "CUSTOMER_ORDER", objectId: row.customerOrderId}}
                    />
                ),
            ]}
        </>
    );

    async function releaseCustomerOrder() {
        const confirmed = await confirm({
            message: i18n.t("release_customer_order_confirmation"),
        });
        if (!confirmed) return;

        const {configuredLinesWithChangedProductFamilyVersion, hasIncompleteSalesConditions} =
            await CustomerOrderReleaseApi.getCustomerOrderReleasePreInfo({
                customerOrderId: row.customerOrderId,
            });

        if (hasIncompleteSalesConditions) {
            const confirmed = await confirm({
                message: i18n.t("customer_order_has_incomplete_sales_conditions_still_release"),
            });
            if (!confirmed) return;
        }
        if (configuredLinesWithChangedProductFamilyVersion.length > 0) {
            const confirmed = await confirm({
                title: i18n.t("changed_configurator_version"),
                message: i18n.t("order_lines_has_changed_configurator_version_after_configuring", {
                    lineNumbers: configuredLinesWithChangedProductFamilyVersion
                        .map((line) => line.lineNumber)
                        .join(", "),
                }),
            });
            if (!confirmed) return;
        }
        await CustomerOrderReleaseApi.releaseCustomerOrder({
            customerOrderId: row.customerOrderId,
        });
        await refreshData();
        customerOrderLinesRefreshRef?.refresh();
    }
};
